import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown, CheckCircle2, MessageSquareText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import StudioSelect from "@/components/masterOrder/StudioSelect";

const DIRECTIONS = [
  { value: "", label: "Все вопросы" },
  { value: "masters", label: "Мастера и специалисты" },
  { value: "suppliers", label: "Поставщики и производство" },
  { value: "services", label: "Услуги" },
  { value: "work", label: "Работа и сотрудничество" },
];
const validSection = (value) => DIRECTIONS.some((item) => item.value === value) ? value : "";
const directionName = (section) => DIRECTIONS.find((item) => item.value === section)?.label || "";
const fieldClass = "w-full min-h-[44px] rounded-xl border border-[#80624E]/25 bg-white/75 px-4 py-3 text-sm text-[#3C2D23] outline-none focus:border-[#80624E] focus:ring-2 focus:ring-[#80624E]/10";
const cardClass = "rounded-[20px] border border-[#765944]/20 bg-white/70 p-5 shadow-[0_5px_19px_rgba(74,59,50,0.045)]";

function QuestionCard({ question, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const answers = Array.isArray(question.answers) ? question.answers : [];
  const panelId = "pm-question-answers-" + question.id;
  return (
    <article className={cardClass} data-testid={"pm-question-card-" + question.id}>
      <button type="button" onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded} aria-controls={panelId}
        className="flex min-h-[44px] w-full items-start justify-between gap-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]"
        data-testid={"pm-question-accordion-" + question.id}>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] tracking-[0.08em] text-[#856D5D]">{directionName(question.section)}</span>
          <span className="mt-2 block font-serif text-lg leading-snug text-[#3A2B22]">{question.title}</span>
          <span className="mt-2 block text-xs text-[#796658]">
            {question.resolved ? "Решено" : answers.length ? "Есть ответы (" + answers.length + ")" : "Ожидает ответа"}
          </span>
        </span>
        <ChevronDown aria-hidden="true" className={"mt-1 h-5 w-5 shrink-0 text-[#715B4A] transition-transform " + (expanded ? "rotate-180" : "")} />
      </button>
      {expanded && <div id={panelId} className="mt-4 space-y-3 border-t border-[#765944]/15 pt-4"
        data-testid={"pm-question-expanded-" + question.id}>
        {question.description && <p className="whitespace-pre-wrap text-sm leading-6 text-[#705D4E]">{question.description}</p>}
        {answers.length > 0 ? answers.slice(0, 3).map((answer) => (
          <div key={answer.id} className="rounded-xl bg-[#FBF7EF] p-3">
            <p className="text-xs text-[#806B5C]">{answer.author_name || "Ответ участника"}</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#594638]">{answer.text}</p>
          </div>
        )) : <p className="text-sm text-[#705D4E]">Ответов пока нет.</p>}
        {answers.length > 3 && <p className="text-xs text-[#806B5C]">Ещё ответов: {answers.length - 3}</p>}
        <button type="button" onClick={onOpen}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-[#594435] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]"
          data-testid={"pm-question-open-discussion-" + question.id}>
          Открыть обсуждение и ответить <ArrowRight className="h-4 w-4" aria-hidden="true"/>
        </button>
      </div>}
    </article>
  );
}

export function QuestionsSpotlight({ section = "" }) {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: [], failed: false });
  const scoped = validSection(section);
  useEffect(() => {
    let live = true;
    setState({ loading: true, data: [], failed: false });
    api.get("/professional-map/questions", { params: { section: scoped || undefined, limit: 3 } })
      .then(({ data }) => { if (live) setState({ loading: false, data: data.results || [], failed: false }); })
      .catch(() => { if (live) setState({ loading: false, data: [], failed: true }); });
    return () => { live = false; };
  }, [scoped]);
  const destination = "/professional-map/questions" + (scoped ? "?section=" + scoped : "");
  return (
    <section className="mt-10 rounded-[25px] border border-[#765944]/20 bg-[#FBF7EF]/65 px-5 py-7 sm:px-8"
      aria-label="Профессиональные вопросы" data-testid="pm-questions-spotlight">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#896F5A]">Знания и опыт коллег</p>
          <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Профессиональные вопросы</h2>
          <p className="mt-3 max-w-xl text-sm leading-6 text-[#6C5747]">
            Вопросы и ответы участников индустрии, к которым можно вернуться.
          </p>
        </div>
        <button type="button" onClick={() => nav(destination)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#765944]/30 bg-[#FBF7EF]/90 px-5 py-3 text-sm font-medium text-[#594435] hover:bg-white">
          Все вопросы <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      {state.loading ? <p className="mt-5 text-sm text-[#806C5D]">Загружаем вопросы…</p>
        : state.failed ? <p className="mt-5 text-sm text-[#806C5D]" role="status">Не удалось загрузить вопросы.</p>
        : state.data.length ? (
          <div className="mt-5 grid gap-3 lg:grid-cols-3">
            {state.data.map((question) => <QuestionCard key={question.id} question={question}
              onOpen={() => nav("/professional-map/questions/" + encodeURIComponent(question.id))} />)}
          </div>
        ) : <p className="mt-5 text-sm text-[#806C5D]" role="status">
          Пока нет опубликованных вопросов. Задай первый вопрос.
        </p>}
    </section>
  );
}

function AskQuestion({ onSubmitted, initialSection }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState(initialSection || "masters");
  const [question, setQuestion] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    const text = question.trim();
    if (!user || busy || !consent || text.length < 5) return;
    // One visible question field, mapped to the existing validated public Q&A schema.
    // A long question retains all content without introducing another user-facing field.
    const firstBreak = text.indexOf("\n");
    const headingLength = firstBreak >= 5 && firstBreak <= 160 ? firstBreak : 160;
    setBusy(true);
    try {
      const { data } = await api.post("/professional-map/questions", {
        section,
        title: text.slice(0, headingLength).trim(),
        description: text.slice(headingLength).trim(),
        publication_consent: true,
      });
      setQuestion(""); setConsent(false);
      toast.success(data?.state === "published" ? "Вопрос опубликован" : "Вопрос отправлен на проверку");
      onSubmitted?.();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className={cardClass} aria-label="Задать вопрос коллегам" data-testid="pm-question-compose">
      <h2 className="font-serif text-2xl">Задать вопрос</h2>
      {!user ? (
        <div className="mt-3">
          <p className="text-sm leading-6 text-[#705C4D]">Для отправки вопроса потребуется вход в аккаунт. Читать опубликованные ответы можно без регистрации.</p>
          <button type="button" onClick={() => nav("/login")}
            className="mt-4 min-h-[44px] rounded-xl border border-[#765944]/30 px-5 text-sm">Войти</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div role="group" aria-labelledby="pm-question-direction-label">
            <div id="pm-question-direction-label" className="mb-1 text-xs text-[#725D4D]">Направление</div>
            <StudioSelect
              value={section}
              onChange={setSection}
              options={DIRECTIONS.filter((item) => item.value)}
              searchable
              searchPlaceholder="Найти направление"
              testid="pm-question-direction"
            />
          </div>
          <label className="block text-xs text-[#725D4D]" htmlFor="pm-question-text">Ваш вопрос *
            <textarea id="pm-question-text" className={fieldClass + " mt-1 resize-y"}
              rows={4} value={question} onChange={(e) => setQuestion(e.target.value)}
              minLength={5} maxLength={3500} required
              placeholder="Напиши свой вопрос…" data-testid="pm-question-text" />
          </label>
          <label className="flex items-start gap-3 text-xs leading-5 text-[#654F42]">
            <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-1" data-testid="pm-question-consent" />
            <span>Согласен(на) на публикацию текста вопроса и ответов с модерацией.
              Контактные данные аккаунта не публикуются. Подробнее: <a className="underline" href="/legal/privacy">конфиденциальность</a>.</span>
          </label>
          <button type="submit" disabled={busy || !consent}
            className="min-h-[44px] rounded-xl border border-[#765944]/30 bg-[#FBF7EF]/90 px-6 text-sm font-medium text-[#594435] disabled:opacity-50"
            data-testid="pm-question-submit">{busy ? "Отправляем…" : "Задать вопрос"}</button>
        </form>
      )}
    </section>
  );
}

export default function ProfessionalQuestionsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeSection = validSection(params.get("section") || "");
  const openMineFromCabinet = params.get("mine") === "1";
  const fromAccount = params.get("from") === "account";
  const returnToOrigin = fromAccount ? "/account" : "/professional-map";
  const detailQuery = fromAccount ? "?from=account&mine=1" : "";
  const [questions, setQuestions] = useState({ loading: true, failed: false, results: [] });
  const [mine, setMine] = useState([]);
  const [minePage, setMinePage] = useState(0);
  const [hasMoreMine, setHasMoreMine] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    let live = true;
    setQuestions({ loading: true, failed: false, results: [], total: 0 });
    api.get("/professional-map/questions", { params: {
      section: activeSection || undefined, q: searchTerm || undefined, limit: 30, skip: page * 30,
    } })
      .then(({ data }) => { if (live) setQuestions({
        loading: false, failed: false, results: data.results || [], total: data.total || 0,
      }); })
      .catch(() => { if (live) setQuestions({ loading: false, failed: true, results: [] }); });
    return () => { live = false; };
  }, [activeSection, searchTerm, page, reload]);

  useEffect(() => {
    let live = true;
    if (!user) { setMine([]); return () => { live = false; }; }
    api.get("/professional-map/questions/mine", { params: { limit: 30, skip: minePage * 30 } })
      .then(({ data }) => { if (live) {
        const batch = Array.isArray(data) ? data : [];
        setMine((old) => minePage === 0 ? batch : [...old, ...batch]);
        setHasMoreMine(batch.length === 30);
      } })
      .catch(() => { if (live) { setMine([]); setHasMoreMine(false); } });
    return () => { live = false; };
  }, [user, minePage, reload]);

  useEffect(() => {
    if (!user || !openMineFromCabinet) return;
    document.getElementById("pm-my-questions")?.scrollIntoView({ block: "start" });
  }, [user, openMineFromCabinet]);

  function filterBy(next) {
    const filtered = new URLSearchParams(params);
    if (next) filtered.set("section", next); else filtered.delete("section");
    setPage(0);
    setParams(filtered);
  }
  function afterSubmitted() { setComposeOpen(false); setMinePage(0); setReload((value) => value + 1); }
  async function hideMine(id) {
    try {
      await api.post("/professional-map/questions/mine/" + encodeURIComponent(id) + "/hide");
      toast.success("Вопрос скрыт");
      setMinePage(0);
      setReload((value) => value + 1);
    } catch (error) { toast.error(formatApiError(error)); }
  }

  return (
    <div className="min-h-screen bg-[#F7F0E5] text-[#3A2B22]" data-testid="pm-questions-page">
      <main className="mx-auto max-w-[1120px] px-5 pb-20 pt-7 sm:px-9">
        <button type="button" onClick={() => nav(returnToOrigin)}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm text-[#705B4A]" data-testid="pm-questions-back-to-origin">
          <ArrowLeft className="h-4 w-4" /> {fromAccount ? "Мой кабинет" : "К Профессиональной карте"}
        </button>
        <div className="mt-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#876D59]">Опыт кондитерской индустрии</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">Профессиональные вопросы</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-[#705C4D]">Задавай вопросы и находи ответы специалистов и коллег.</p>
          </div>
          <div className="flex flex-wrap gap-2">
          {user && <button type="button" onClick={() => document.getElementById("pm-my-questions")?.scrollIntoView({ behavior: "smooth" })}
            className="min-h-[44px] rounded-xl border border-[#765944]/30 px-4 text-sm">Мои вопросы</button>}
          <button type="button" onClick={() => setComposeOpen((value) => !value)}
            className="min-h-[44px] rounded-xl border border-[#765944]/30 bg-[#FBF7EF] px-5 text-sm font-medium text-[#594435] hover:bg-white"
            data-testid="pm-question-compose-toggle">{composeOpen ? "Закрыть форму" : "Задать вопрос"}</button>
          </div>
        </div>
        {composeOpen && <div className="mt-6"><AskQuestion onSubmitted={afterSubmitted} initialSection={activeSection || "masters"} /></div>}
        <form onSubmit={(e) => { e.preventDefault(); setPage(0); setSearchTerm(searchInput.trim()); }}
          className="mt-6 flex flex-wrap gap-2" role="search" aria-label="Поиск по вопросам и ответам">
          <label className="sr-only" htmlFor="pm-questions-search">Поиск по вопросам и ответам</label>
          <input id="pm-questions-search" type="search" maxLength={180} value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Найти вопрос или ответ…"
            className={fieldClass + " flex-1 min-w-[170px]"} data-testid="pm-questions-search"/>
          <button type="submit" className="min-h-[44px] rounded-xl border border-[#765944]/30 bg-[#FBF7EF] px-5 text-sm">Найти</button>
          {searchTerm && <button type="button"
            onClick={() => { setSearchInput(""); setSearchTerm(""); setPage(0); }}
            className="min-h-[44px] rounded-xl border border-[#765944]/30 px-4 text-sm">Сбросить</button>}
        </form>
        <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Темы вопросов">
          {DIRECTIONS.map((item) => (
            <button key={item.value || "all"} type="button" onClick={() => filterBy(item.value)}
              aria-pressed={activeSection === item.value}
              className={"min-h-[40px] rounded-full border px-3 py-2 text-xs " +
                (activeSection === item.value
                  ? "border-[#765944]/50 bg-[#EAE0D3] text-[#3A2B22]"
                  : "border-[#765944]/25 bg-white/65 text-[#665040]")}>{item.label}</button>
          ))}
        </div>
        <section className="mt-6" aria-label="Опубликованные вопросы" data-testid="pm-question-results">
          {questions.loading ? <p className="py-8 text-sm text-[#705C4D]">Загружаем вопросы…</p>
            : questions.failed ? <p role="status" className="py-8 text-sm">Не удалось загрузить вопросы. Повторите попытку.</p>
            : !questions.results.length ? (
              <p className={cardClass + " text-sm leading-6 text-[#705C4D]"} role="status">
                По этому запросу пока нет опубликованных вопросов.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {questions.results.map((question) => <QuestionCard key={question.id} question={question}
                  onOpen={() => nav("/professional-map/questions/" + encodeURIComponent(question.id) + detailQuery)}/>)}
              </div>
            )}
        </section>
        {questions.total > 30 && <nav className="mt-4 flex items-center justify-end gap-3" aria-label="Страницы вопросов">
          <button type="button" disabled={page === 0} onClick={() => setPage((n) => Math.max(0, n - 1))}
            className="min-h-[40px] rounded-lg border border-[#765944]/25 px-4 disabled:opacity-40">Назад</button>
          <span className="text-xs">{page + 1} / {Math.ceil(questions.total / 30)}</span>
          <button type="button" disabled={(page + 1) * 30 >= questions.total}
            onClick={() => setPage((n) => n + 1)}
            className="min-h-[40px] rounded-lg border border-[#765944]/25 px-4 disabled:opacity-40">Далее</button>
        </nav>}
        {Boolean(user) && (
          <section id="pm-my-questions" className="mt-10 scroll-mt-6" aria-labelledby="pm-my-questions-title">
            <h2 id="pm-my-questions-title" className="font-serif text-2xl">Мои вопросы</h2>
            {mine.length === 0 && <p className="mt-3 text-sm text-[#705C4D]">У тебя пока нет вопросов.</p>}
            <div className="mt-4 space-y-3">
              {mine.map((item) => (
                <div className={cardClass} key={item.id}>
                  {item.state === "published" ? <button type="button"
                    onClick={() => nav("/professional-map/questions/" + encodeURIComponent(item.id) + detailQuery)}
                    className="font-serif text-lg text-left underline-offset-4 hover:underline">{item.title}</button>
                    : <p className="font-serif text-lg">{item.title}</p>}
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-[#705C4D]">
                    <span>{({ pending: "На модерации", published: "Опубликовано",
                      rejected: "Отклонено", hidden: "Скрыто" })[item.state] || item.state}</span>
                    {["pending", "published"].includes(item.state) && (
                      <button type="button" onClick={() => hideMine(item.id)}
                        className="min-h-[36px] rounded-lg border border-[#765944]/25 px-3">Скрыть вопрос</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {hasMoreMine && <button type="button" onClick={() => setMinePage((n) => n + 1)}
              className="mt-4 min-h-[44px] rounded-xl border border-[#765944]/25 px-4 text-sm"
              data-testid="pm-load-more-my-questions">Показать ещё мои вопросы</button>}
          </section>
        )}
      </main>
    </div>
  );
}

export function ProfessionalQuestionDetail() {
  const { questionId } = useParams();
  const nav = useNavigate();
  const [queryParams] = useSearchParams();
  const fromAccount = queryParams.get("from") === "account";
  const questionsBack = fromAccount ? "/professional-map/questions?mine=1&from=account" : "/professional-map/questions";
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, question: null });
  const [answerText, setAnswerText] = useState("");
  const [answerAuthor, setAnswerAuthor] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ loading: true, question: null });
    api.get("/professional-map/questions/" + encodeURIComponent(questionId || ""))
      .then(({ data }) => { if (live) setState({ loading: false, question: data }); })
      .catch(() => { if (live) setState({ loading: false, question: null }); });
    return () => { live = false; };
  }, [questionId, refresh]);
  async function sendAnswer(e) {
    e.preventDefault();
    if (!user || submitting || !consent || !answerText.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/professional-map/questions/" + encodeURIComponent(questionId) + "/answers", {
        text: answerText.trim(), public_author_name: answerAuthor.trim(), publication_consent: true,
      });
      setAnswerText(""); setAnswerAuthor(""); setConsent(false);
      toast.success(data.state === "published" ? "Ответ опубликован" : "Ответ отправлен на проверку");
      setRefresh((n) => n + 1);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  }
  const question = state.question;
  return (
    <div className="min-h-screen bg-[#F7F0E5] text-[#3A2B22]" data-testid="pm-question-detail">
      <main className="mx-auto max-w-[860px] px-5 pb-20 pt-7 sm:px-9">
        <button type="button" onClick={() => nav(questionsBack)}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm text-[#705C4D]" data-testid="pm-question-detail-back">
          <ArrowLeft className="h-4 w-4" /> Все вопросы
        </button>
        {state.loading ? <p className="mt-8 text-sm">Загрузка…</p>
          : !question ? <p className={cardClass + " mt-8"} role="status">Вопрос не найден или пока не опубликован.</p>
          : (
            <article className="mt-7 space-y-5">
              <div className={cardClass}>
                <p className="text-xs text-[#8A715D]">{directionName(question.section)}</p>
                <h1 className="mt-3 font-serif text-3xl">{question.title}</h1>
                <p className="mt-5 whitespace-pre-wrap text-sm leading-7 text-[#604D3D]">{question.description}</p>
                {question.tried && <>
                  <h2 className="mt-5 font-serif text-xl">Что уже пробовали</h2>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-7">{question.tried}</p>
                </>}
                <p className="mt-5 text-xs text-[#806B5C]">{question.resolved ? "Решено" : "Обсуждение открыто"}</p>
              </div>
              <h2 className="font-serif text-2xl">Ответы участников</h2>
              {question.answers?.length ? question.answers.map((answer) => (
                <div className={cardClass} key={answer.id}>
                  <p className="mb-3 text-xs text-[#806B5C]">{answer.author_name || "Ответ редакции"}</p>
                  <p className="whitespace-pre-wrap text-sm leading-7 text-[#594638]">{answer.text}</p>
                </div>
              )) : <p className="text-sm text-[#705C4D]">Ответов пока нет. Ты можешь ответить первым.</p>}
              <section className={cardClass} aria-label="Ответить на вопрос">
                <h2 className="font-serif text-xl">Ответить</h2>
                {!user ? <p className="mt-3 text-sm">Чтобы ответить, <button type="button"
                  className="underline" onClick={() => nav("/login")}>войди в аккаунт</button>.</p>
                : <form onSubmit={sendAnswer} className="mt-4 space-y-4">
                    <label className="block text-sm" htmlFor="pm-community-answer">Твой ответ</label>
                    <textarea id="pm-community-answer" rows={4} minLength={2} maxLength={5000}
                      required value={answerText} onChange={(e) => setAnswerText(e.target.value)}
                      className={fieldClass} placeholder="Поделись своим опытом или решением."
                      data-testid="pm-community-answer"/>
                    <label className="block text-sm" htmlFor="pm-public-answer-name">Как подписать ответ (необязательно)</label>
                    <input id="pm-public-answer-name" type="text" maxLength={90}
                      value={answerAuthor} onChange={(e) => setAnswerAuthor(e.target.value)}
                      className={fieldClass} placeholder="Имя или профессиональный псевдоним"/>
                    <label className="flex items-start gap-2 text-xs">
                      <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5" />
                      <span>Согласен(на) на публичное размещение ответа и указанной подписи.</span>
                    </label>
                    <button type="submit" disabled={!consent || submitting || answerText.trim().length < 2}
                      className="min-h-[44px] rounded-xl border border-[#765944]/30 bg-[#FBF7EF] px-5 text-sm disabled:opacity-40">
                      {submitting ? "Отправляем…" : "Отправить ответ"}
                    </button>
                  </form>}
              </section>
            </article>
          )}
      </main>
    </div>
  );
}
